import React from 'react'
import { useMemo } from 'react'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import {
  makeLink,
  OtherColumn,
  FirstColumn,
  HookOptions,
  GroupColumnsType,
  createNumericColumn,
  formatNameWithAdditionalInfo,
  getPerformanceStatsColumnTranslationKey,
} from './useStatsColumns'
import dayjs from 'dayjs'
import { PublisherCostDetails, PublisherPerformanceStatsPartsFragment, Status } from '../../../__generated__/graphql'
import LinkCell from '../../../components/layout/cells/LinkCell'
import PerformanceStatsTableSelectField from '../../../components/stats/table/header/PerformanceStatsTableSelectField'
import ExpandCellWrapper from '../../../components/table/ExpandCellWrapper'
import {
  BASE_STAT_DATA_TYPE,
  COMPANY_BASE_STAT_DATA_TYPE,
  PositionsPerformanceStatsTableItem,
  POSITION_DATA_TYPE,
  PUBLISHER_DATA_TYPE,
  RESELLER_PUBLISHER_DATA_TYPE,
  WEBSITE_DATA_TYPE,
} from '../../../interfaces/stats'
import { useAppTranslations } from '../useAppTranslations'
import { Column } from '../../../components/table/ReactTable'
import { useIsPwsUser } from '../useUserContext'
import { StatusStyled } from '../../../components/StatusStyled'

type TotalPerformanceStats = PublisherPerformanceStatsPartsFragment & { name: string }

const positionColumnHelper = createColumnHelper<PositionsPerformanceStatsTableItem>()

const AdvertiserMetrics: string[] = [
  PUBLISHER_DATA_TYPE.RESELLERS,
  PUBLISHER_DATA_TYPE.CAMPAIGNS,
  POSITION_DATA_TYPE.ZONES,
  RESELLER_PUBLISHER_DATA_TYPE.ADVERTISERS,
]

const getPerformanceMetrics = (dataType: string): GroupColumnsType => {
  const isPublisherMetrics = !AdvertiserMetrics.includes(dataType)
  const impressionMetrics = isPublisherMetrics ? 'displays' : 'impressions'

  return {
    group: 1,
    columnsName: [impressionMetrics, 'ctr', 'clicks'],
  }
}

const REVENUE_METRICS: GroupColumnsType = {
  group: 2,
  columnsName: ['cpc', 'rpm', 'cpm', 'commissions'],
}

const LEAD_METRICS = {
  group: 3,
  columnsName: ['leads' /*, 'lr'*/],
}

const useNameColumnPublisher = <T extends string>(
  dataType: string,
  total: TotalPerformanceStats,
  options: HookOptions<T>
): Column => {
  const { dateFormat, timezone, selectedDataType, selectedDataTypeChange, allDataTypes } = options
  const columnHelper = createColumnHelper<
    PublisherCostDetails & { name: string; id?: string; slug?: string; additionalInfo?: string }
  >()

  return useMemo(
    () =>
      columnHelper.accessor('name', {
        header: () => (
          <FirstColumn>
            <PerformanceStatsTableSelectField
              defaultValue={selectedDataType}
              onChange={selectedDataTypeChange}
              options={allDataTypes}
            />
          </FirstColumn>
        ),
        enableSorting: false,
        cell: ({ row, getValue }) => {
          const dateOrName = getValue()
          const maybeDate = dayjs(dateOrName)

          const titleContent = (() => {
            if (maybeDate.isValid() && selectedDataType === BASE_STAT_DATA_TYPE.TIMESERIES) {
              const dataWithTimezone = maybeDate.utcOffset() > 0 ? maybeDate : maybeDate.tz(timezone)

              return <>{dataWithTimezone.format(dateFormat)}</>
            } else
              return (
                <LinkCell
                  value={formatNameWithAdditionalInfo(dateOrName, row.original?.additionalInfo)}
                  target={makeLink({
                    type: selectedDataType,
                    id: row.original?.id ?? row.original?.slug,
                    row,
                  })}
                  label={dateOrName}
                />
              )
          })()

          return <ExpandCellWrapper row={row}>{titleContent}</ExpandCellWrapper>
        },
        footer: total.name,
      }) as Column,
    [dataType, total]
  )
}

const useNumericColumnsGroup = (
  group: GroupColumnsType,
  total: TotalPerformanceStats
): ColumnDef<PublisherCostDetails>[] => {
  const t = useAppTranslations()
  return useMemo(
    () =>
      group.columnsName.map((columnName) =>
        createNumericColumn<PublisherCostDetails>(
          columnName,
          t(getPerformanceStatsColumnTranslationKey(columnName)),
          group.group,
          total
        )
      ),
    [group, total]
  )
}

const useAdditionalColumnsPublisher = (dataType: string): Column[] => {
  const t = useAppTranslations()

  return useMemo(() => {
    switch (dataType) {
      case COMPANY_BASE_STAT_DATA_TYPE.WEBSITES_AND_POSITIONS:
      case WEBSITE_DATA_TYPE.POSITIONS:
        return [
          positionColumnHelper.accessor('channelType', {
            header: () => <OtherColumn>{t('stats.table.channelType') as string}</OtherColumn>,
          }),
          positionColumnHelper.accessor('status', {
            header: t('commons.status') as string,
            cell: (info) => {
              if (!info.getValue()) return ''
              return <StatusStyled status={info.getValue() as Status} />
            },
          }),
        ] as Column[]
      default:
        return []
    }
  }, [dataType])
}

export const usePublisherStatsColumns = <T extends string>(
  dataType: string,
  total: TotalPerformanceStats,
  options: HookOptions<T>
): Column[] => {
  const isPwsUser = useIsPwsUser()
  const displayLeadAndOrderMetrics = isPwsUser

  const nameColumn = useNameColumnPublisher(dataType, total, options)
  const performanceColumns = useNumericColumnsGroup(getPerformanceMetrics(dataType), total)
  const revenueColumns = useNumericColumnsGroup(REVENUE_METRICS, total)
  const leadColumns = useNumericColumnsGroup(LEAD_METRICS, total)
  const additionalColumns = useAdditionalColumnsPublisher(dataType)

  return [
    nameColumn,
    ...additionalColumns,
    ...performanceColumns,
    ...(displayLeadAndOrderMetrics ? leadColumns : []),
    ...revenueColumns,
  ] as Column[]
}
